import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { asOwnerRoutes, getConsoleAlarms, getConsoleBusinesses } from '../lib/api'
import { loadReason, unacknowledgedCount } from '../lib/consoleStatus'
import { ConsoleShell, SectionMessage } from '../components/console/ConsoleShell'
import { OwnerHome, supportBanner, type OwnerToday, type SupportView } from '../components/soc/OwnerHome'
import { linkClass } from '../components/soc/SocShell'

// BRIEF-F3, support view: a business's own page (OwnerHome) exactly as its
// owner sees it, fed by /hub/console/businesses/{id}/as-owner/*. Staff
// only, read-only, every call logged on the server; never a token, a
// session or a login as the customer. The banner stays fixed so no one
// mistakes this for the console's own page or for acting as the owner.

export function ConsoleSupportViewPage() {
  const { tenantId = '' } = useParams<{ tenantId: string }>()
  const routes = useMemo(() => asOwnerRoutes(tenantId), [tenantId])
  const [today, setToday] = useState<OwnerToday | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [business, setBusiness] = useState<string | null>(null)
  const [alarmCount, setAlarmCount] = useState<number | null>(null)

  useEffect(() => {
    let cancelled = false
    setToday(null)
    setError(null)
    routes
      .today()
      .then((t) => !cancelled && setToday(t as OwnerToday))
      .catch((err) => !cancelled && setError(loadReason(err)))
    getConsoleBusinesses()
      .then((rows) => !cancelled && setBusiness(rows.find((b) => b.tenant_id === tenantId)?.name ?? null))
      .catch(() => !cancelled && setBusiness(null))
    getConsoleAlarms()
      .then((a) => !cancelled && setAlarmCount(unacknowledgedCount(a)))
      .catch(() => !cancelled && setAlarmCount(null))
    return () => {
      cancelled = true
    }
  }, [routes, tenantId])

  const name = business ?? 'this business'
  const support: SupportView = { business: name, loaders: routes }

  return (
    <ConsoleShell alarmCount={alarmCount}>
      <div
        role="note"
        data-testid="support-banner"
        className="sticky top-0 z-20 -mb-4 rounded-[10px] border-2 border-brass bg-navy px-5 py-3.5 text-[16px] font-bold text-cream shadow-md"
      >
        {supportBanner(name)}
      </div>
      <Link to={`/console/business/${encodeURIComponent(tenantId)}`} className={linkClass}>
        ← Back to the business
      </Link>
      {error !== null ? (
        <SectionMessage tone="error">Could not load their page: {error}</SectionMessage>
      ) : today === null ? (
        <SectionMessage>Loading their page…</SectionMessage>
      ) : (
        <div data-testid="support-view" className="flex flex-col gap-9">
          <OwnerHome key={tenantId} today={today} support={support} />
        </div>
      )}
    </ConsoleShell>
  )
}
